const fallbackKey = 'saveflow:workspaceSessionFallback';

export async function getJson(url, options = {}) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    return parseResponse(response, options);
}

export async function postJson(url, body, options = {}) {
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    return parseResponse(response, options);
}

export async function parseResponse(response, options = {}) {
    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.ok) {
        if (options.redirectAuth !== false) {
            redirectForAuth(payload);
        }
        throw new Error(payload?.error?.message || 'SaveFlow could not complete that request.');
    }

    return payload.data;
}

export function redirectForAuth(payload) {
    if (payload?.error?.code !== 'AUTH_REQUIRED') {
        return;
    }

    const next = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    window.history.pushState({}, '', `/account.html?next=${next}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
}

export async function loadWorkspaceSession() {
    try {
        return await getJson('/api/workspace-session.php', { redirectAuth: false });
    } catch {
        return { session: readSessionFallback() };
    }
}

export async function loadWorkspaceResume() {
    try {
        return await getJson('/api/workspace-resume.php', { redirectAuth: false });
    } catch {
        return {
            session: readSessionFallback(),
            unfinished_jobs: [],
            queue: { counts: {}, resumable_items: [], next_item: null },
            vault: { active_count: 0 },
            recent_exports: [],
        };
    }
}

export async function saveWorkspaceSession(payload) {
    const normalized = normalizeSessionPayload(payload);
    localStorage.setItem(fallbackKey, JSON.stringify(normalized));

    try {
        return await postJson('/api/workspace-session-save.php', normalized, { redirectAuth: false });
    } catch {
        return { session: normalized };
    }
}

export async function clearWorkspaceSession() {
    localStorage.removeItem(fallbackKey);

    try {
        return await postJson('/api/workspace-session-clear.php', {}, { redirectAuth: false });
    } catch {
        return { cleared: true };
    }
}

export function formFilters(form) {
    return Object.fromEntries(new FormData(form).entries());
}

export function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
}

export function escapeAttribute(value) {
    return String(value ?? '').replaceAll('"', '&quot;');
}

export function cssEscape(value) {
    if (window.CSS?.escape) {
        return window.CSS.escape(String(value));
    }

    return String(value).replaceAll('"', '\\"');
}

export function formatBytes(value) {
    const bytes = Number(value || 0);

    if (bytes <= 0) {
        return 'Unknown';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unit = 0;

    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }

    return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function listen(target, event, handler) {
    target?.addEventListener(event, handler);
    return () => target?.removeEventListener(event, handler);
}

export function cleanupAll(cleanups) {
    return () => cleanups.forEach((cleanup) => cleanup?.());
}

export function installSavedFilters({ scope, form, statusText, onApply }) {
    const bar = document.querySelector(`[data-filter-scope="${scope}"]`);

    if (!bar || !form) {
        return () => {};
    }

    const nameInput = bar.querySelector('[data-filter-name]');
    const list = bar.querySelector('[data-filter-list]');
    const save = bar.querySelector('[data-save-filter]');
    const apply = bar.querySelector('[data-apply-filter]');
    let filters = [];

    const load = async () => {
        const data = await getJson(`/api/saved-filters.php?scope=${encodeURIComponent(scope)}`);
        filters = data.items || [];
        list.innerHTML = '<option value="">Saved filters</option>' + filters.map((item) => {
            return `<option value="${item.id}">${escapeHtml(item.name)}</option>`;
        }).join('');
    };

    const saveHandler = async () => {
        try {
            await postJson('/api/saved-filters-save.php', {
                scope,
                name: nameInput.value,
                filters: formFilters(form),
            });
            nameInput.value = '';
            await load();
            statusText.textContent = 'Filter saved.';
        } catch (error) {
            statusText.textContent = error.message;
        }
    };

    const applyHandler = () => {
        const id = Number(list.value || 0);
        const selected = filters.find((item) => item.id === id);

        if (!selected) {
            return;
        }

        for (const [name, value] of Object.entries(selected.filters || {})) {
            const field = form.elements.namedItem(name);
            if (field) {
                field.value = value;
            }
        }

        onApply();
    };

    save.addEventListener('click', saveHandler);
    apply.addEventListener('click', applyHandler);
    void load();

    return cleanupAll([
        () => save.removeEventListener('click', saveHandler),
        () => apply.removeEventListener('click', applyHandler),
    ]);
}

export function openActionSheetFromDataset(button) {
    const actions = JSON.parse(button.dataset.sheetActions || '[]');
    window.SaveFlowA11y?.openActionSheet({
        title: button.dataset.sheetTitle || 'Actions',
        returnFocusTo: button,
        actions: actions.map((action) => action.href ? action : {
            label: action.label,
            onSelect: () => document.querySelector(action.selector)?.click(),
        }),
    });
}

function normalizeSessionPayload(payload) {
    const normalized = {
        active_page: payload.active_page || 'workspace',
    };

    for (const key of ['draft_url', 'draft_text', 'filters', 'selected_collection_id', 'queue_context', 'vault_context', 'last_job_id', 'last_result']) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
            normalized[key] = payload[key] || null;
        }
    }

    return normalized;
}

function readSessionFallback() {
    const raw = localStorage.getItem(fallbackKey);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        localStorage.removeItem(fallbackKey);
        return null;
    }
}
